import { useCurrentAccountId } from '@zapier/identity';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { isEmptyObject } from '../utils/isEmptyObject';

import { useLocalStorage } from '@zapier/common-hooks';
import { useMediaProps } from '@zapier/responsive-utils';
import { useQueryTopNav } from './useQueryTopNav';
import type { TopBarLinksType } from '../constants';

import type { ZapierProfile, ZapierAccount } from '../types';
import type { ZapierSession } from '../../types';
import {
  UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
  UNIVERSAL_SIDEBAR_WIDTH,
} from '../constants';
import { useQuerySession } from './useQuerySession';

type UniversalNavContext = {
  accountData?: ZapierAccount;
  accountsListData?: Array<ZapierProfile>;
  canForceSidebarOpen: boolean;
  canTopbarFitBackToAppButton: boolean;
  canTopbarFitLinks: boolean;
  currentAccountId?: number;
  currentUrl: string;
  isLoadingLoggedIn: boolean;
  isLoggedIn: boolean;
  isLoadingTopNav: boolean;
  isOnAppPage: boolean;
  isSearchFullWidth: boolean;
  isSidebarControlledByTopbar: boolean;
  isSidebarForcedOpen: boolean;
  isSidebarOpenedByTopbar: boolean;
  isSidebarRendered: boolean;
  mode?: 'in-app' | 'navigation';
  topNavData: TopBarLinksType;
  onToggleSidebarVisibility?: () => void;
  sessionData?: ZapierSession;
  setIsSidebarForcedOpen: (state: boolean) => void;
  setIsSidebarOpenedByTopbar: (state: boolean) => void;
  setIsSidebarRendered: (state: boolean) => void;
  shouldRenderCallToAction: boolean;
  shouldRenderSearch: boolean;
  shouldRenderSidebar: boolean;
  shouldRenderAccessibleSidebar: boolean;
  shouldRenderTopbarLinks: boolean;
  shouldRenderTOS?: boolean;
  shouldRenderUniversalNav: boolean;
  sidebarWidth: number;
  topbarSize: 'small' | 'medium';
  urlPathPrefix?: string;
  getFullyQualifiedURL: (path?: string | null) => string | undefined;
  apiHostname: string;
};

const defaultContext: UniversalNavContext = {
  apiHostname: '',
  canForceSidebarOpen: false,
  canTopbarFitBackToAppButton: false,
  canTopbarFitLinks: false,
  currentAccountId: undefined,
  currentUrl: '',
  isLoadingLoggedIn: true,
  isLoadingTopNav: true,
  isLoggedIn: false,
  isOnAppPage: false,
  isSearchFullWidth: true,
  isSidebarControlledByTopbar: false,
  isSidebarForcedOpen: false,
  isSidebarOpenedByTopbar: false,
  isSidebarRendered: false,
  onToggleSidebarVisibility: () => {},
  setIsSidebarForcedOpen: () => {},
  setIsSidebarOpenedByTopbar: () => {},
  setIsSidebarRendered: () => {},
  shouldRenderCallToAction: false,
  shouldRenderSearch: true,
  shouldRenderSidebar: false,
  shouldRenderAccessibleSidebar: false,
  shouldRenderTopbarLinks: false,
  shouldRenderTOS: false,
  shouldRenderUniversalNav: true,
  sidebarWidth: 0,
  topbarSize: 'medium',

  topNavData: {},
  // @ts-ignore. This is a default value.
  getFullyQualifiedURL: (path?: string | null) => path || undefined,
};

export const UniversalNavContext = createContext(defaultContext);

export const useUniversalNavContext = () => useContext(UniversalNavContext);

type Props = {
  /**
   * Optionally specify the rendering mode for UniversalLayout:
   *   `in-app`: The logged-in experience including the sidebar menu.
   *   `navigation`: No sidebar, but with navigation links in the topbar.
   *
   * If omitted, the mode will be inferred from the `currentUrl` prop.
   */
  mode?: 'in-app' | 'navigation';
  /*
   * Account data for the current user.
   */
  accountData?: ZapierAccount;
  /**
   * tbd
   */
  accountsListData?: Array<ZapierProfile>;
  /**
   * What to render within the provider.
   */
  children: React.ReactNode;
  /**
   * `id` of the current account. Will be falsey if user isn't logged in.
   */
  currentAccountId?: number;
  /**
   * Current path in the browser's address bar.
   */
  currentUrl: string;
  /**
   * Whether or not to show a `tempory` TOS in the logged in sidebar
   * Temporary feature for Nov/Dec 2022
   */
  shouldRenderTOS?: boolean;
  /**
   * Optional hostname prop that will render as absolute path instead of a relative path.
   */
  urlPathPrefix?: string;
  /*
   * Session data for the current user.
   */
  sessionData?: ZapierSession;
  /**
   * Force API requests to be sent to this hostname, instead of the current domain.
   * Most likely needed for applications running on a subdomain.
   */
  apiHostname?: string;
};

const isCurrentUrlAnAuthPage = (currentUrl: string) => {
  return (
    currentUrl.includes('/login') ||
    currentUrl.includes('/sign-up') ||
    currentUrl.includes('/oauth/authorize')
  );
};

const isCurrentUrlAnAppPage = (currentUrl: string, mode?: string) => {
  if (mode) {
    return mode === 'in-app' ? true : false;
  }
  return currentUrl.includes('/app/');
};

const isCurrentUrlAnOnboardingPage = (currentUrl: string) => {
  return currentUrl.includes('/app/onboarding');
};

const shouldRenderSidebarForRoute = ({
  currentUrl,
  mode,
}: {
  currentUrl: string;
  mode?: Props['mode'];
}) => {
  return (
    isCurrentUrlAnAppPage(currentUrl, mode) &&
    !isCurrentUrlAnAuthPage(currentUrl) &&
    !isCurrentUrlAnOnboardingPage(currentUrl)
  );
};

export const shouldRenderTopbarLinksForRoute = ({
  currentUrl,
  mode,
}: {
  currentUrl: string;
  mode?: Props['mode'];
}) => {
  return (
    !isCurrentUrlAnAppPage(currentUrl, mode) &&
    !isCurrentUrlAnAuthPage(currentUrl)
  );
};

const getShouldRenderSidebar = ({
  isLoggedIn,
  canTopbarFitLinks,
  currentUrl,
  isSidebarControlledByTopbar,
  isSidebarOpenedByTopbar,
  mode,
}: {
  isLoggedIn: boolean;
  canTopbarFitLinks: boolean;
  currentUrl: string;
  isSidebarControlledByTopbar: boolean;
  isSidebarOpenedByTopbar: boolean;
  mode?: Props['mode'];
}) => {
  if (isSidebarControlledByTopbar) {
    return isSidebarOpenedByTopbar;
  }
  if (mode && mode === 'navigation') {
    return canTopbarFitLinks === false ? true : false;
  }

  if (mode && mode === 'in-app' && isLoggedIn) {
    return true;
  }

  if (isLoggedIn && shouldRenderSidebarForRoute({ currentUrl, mode })) {
    return true;
  }
  if (
    !canTopbarFitLinks &&
    shouldRenderTopbarLinksForRoute({ currentUrl, mode })
  ) {
    return true;
  }
  return false;
};

const getShouldRenderAccessibleSidebar = ({
  isLoggedIn,
  canTopbarFitLinks,
  currentUrl,
  isSidebarControlledByTopbar,
  isSidebarOpenedByTopbar,
  mode,
}: {
  isLoggedIn: boolean;
  canTopbarFitLinks: boolean;
  currentUrl: string;
  isSidebarControlledByTopbar: boolean;
  isSidebarOpenedByTopbar: boolean;
  mode?: Props['mode'];
}) => {
  if (isSidebarControlledByTopbar) {
    return !isSidebarOpenedByTopbar;
  }
  if (mode && mode === 'navigation') {
    return canTopbarFitLinks === false ? true : false;
  }

  if (mode && mode === 'in-app' && isLoggedIn && canTopbarFitLinks) {
    return false;
  }

  if (
    isLoggedIn &&
    shouldRenderSidebarForRoute({ currentUrl, mode }) &&
    !canTopbarFitLinks
  ) {
    return true;
  }
  if (
    !canTopbarFitLinks &&
    shouldRenderTopbarLinksForRoute({ currentUrl, mode })
  ) {
    return true;
  }
  return false;
};

const getShouldRenderSearch = ({ currentUrl }: { currentUrl: string }) => {
  if (
    isCurrentUrlAnAuthPage(currentUrl) ||
    isCurrentUrlAnOnboardingPage(currentUrl)
  ) {
    return false;
  }
  return true;
};

const getShouldRenderTopbarToggle = ({
  isSidebarControlledByTopbar,
  isLoggedIn,
  currentUrl,
  mode,
}: {
  isSidebarControlledByTopbar: boolean;
  isLoggedIn: boolean;
  currentUrl: string;
  mode?: Props['mode'];
}) => {
  // If the sidebar isn't controlled by the topbar then
  // the toggle should never render, so bail immediately.
  if (!isSidebarControlledByTopbar) {
    return false;
  }
  if (isLoggedIn) {
    return true;
  }
  return shouldRenderTopbarLinksForRoute({
    currentUrl,
    mode,
  });
};

const getSidebarWidth = ({
  canForceSidebarOpen,
  isSidebarForcedOpen,
  isSidebarRendered,
  isSidebarControlledByTopbar,
}: {
  canForceSidebarOpen: boolean;
  isSidebarForcedOpen: boolean;
  isSidebarRendered: boolean;
  isSidebarControlledByTopbar: boolean;
}) => {
  if (!isSidebarRendered || isSidebarControlledByTopbar) {
    return 0;
  }
  if (canForceSidebarOpen && isSidebarForcedOpen) {
    return UNIVERSAL_SIDEBAR_WIDTH;
  }
  return UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH;
};

export const UniversalNavProvider = (props: Props) => {
  const { isLoading: isLoadingTopNav, topNav } = useQueryTopNav();
  const {
    accountData: accountUsageData,
    accountsListData,
    currentUrl,
    mode,
    sessionData: sessionDataFromProps,
    shouldRenderTOS,
    urlPathPrefix,
  } = props;
  const isOnAppPage = isCurrentUrlAnAppPage(currentUrl, mode);
  const {
    isSearchFullWidth,
    isSidebarControlledByTopbar,
    canForceSidebarOpen,
    canTopbarFitBackToAppButton,
    canTopbarFitLinks,
    topbarSize,
  } = useMediaProps({
    '(min-width: 0px)': {
      isSidebarControlledByTopbar: true,
      isSearchFullWidth: true,
      canForceSidebarOpen: false,
      canTopbarFitBackToAppButton: false,
      canTopbarFitLinks: false,
      topbarSize: 'small',
    },
    '(min-width: 450px)': {
      canTopbarFitBackToAppButton: isEmptyObject(topNav.callToAction),
      topbarSize: isEmptyObject(topNav.callToAction) ? 'medium' : 'small',
    },
    '(min-width: 650px)': {
      canTopbarFitBackToAppButton: true,
      topbarSize: 'medium',
    },

    '(min-width: 1040px)': {
      isSidebarControlledByTopbar: false,
      isSearchFullWidth: false,
      canTopbarFitLinks: true,
    },
    '(min-width: 1280px)': {
      canForceSidebarOpen: shouldRenderSidebarForRoute({ currentUrl, mode }),
    },
  });
  const shouldRenderTopbarLinks =
    canTopbarFitLinks &&
    shouldRenderTopbarLinksForRoute({
      currentUrl,
      mode,
    });

  const { data: sessionData } = useQuerySession();
  const currentAccountId =
    useCurrentAccountId() || sessionData?.currentAccountId;

  const isLoggedIn = sessionData ? sessionData.isLoggedIn : false;
  const [isSidebarOpenedByTopbar, setIsSidebarOpenedByTopbar] = useState(false);
  const [isSidebarForcedOpen, setIsSidebarForcedOpen] = useLocalStorage(
    'UniversalNav:isSidebarForcedOpen'
  );
  // Default to opening sidebar if the user hasn't chosen yet.
  useEffect(() => {
    if (isSidebarForcedOpen === null || isSidebarForcedOpen === undefined) {
      setIsSidebarForcedOpen(true);
    }
  }, [isSidebarForcedOpen, setIsSidebarForcedOpen]);
  const [isSidebarRendered, setIsSidebarRendered] = useState(false);
  const shouldRenderSidebar = getShouldRenderSidebar({
    isLoggedIn,
    canTopbarFitLinks,
    currentUrl,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
    mode,
  });
  const shouldRenderAccessibleSidebar = getShouldRenderAccessibleSidebar({
    isLoggedIn,
    canTopbarFitLinks,
    currentUrl,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
    mode,
  });
  const shouldRenderSearch = getShouldRenderSearch({
    currentUrl,
  });
  const shouldRenderTopbarToggle = getShouldRenderTopbarToggle({
    isSidebarControlledByTopbar,
    isLoggedIn,
    currentUrl,
  });
  const onToggleSidebarVisibility = shouldRenderTopbarToggle
    ? () => {
        setIsSidebarOpenedByTopbar(!isSidebarOpenedByTopbar);
      }
    : undefined;
  const sidebarWidth = getSidebarWidth({
    canForceSidebarOpen,
    isSidebarForcedOpen,
    isSidebarRendered,
    isSidebarControlledByTopbar,
  });

  const memoizedFullyQualifiedUrl = useCallback(
    (link?: string | null): string | undefined => {
      if (link && urlPathPrefix && link.startsWith('/'))
        return urlPathPrefix + link;

      return link || undefined;
    },
    [urlPathPrefix]
  );

  const value = {
    accountUsageData,
    accountsListData,
    currentAccountId,
    isLoadingTopNav,
    canTopbarFitBackToAppButton,
    canTopbarFitLinks,
    canForceSidebarOpen,
    currentUrl,
    isLoadingLoggedIn: !sessionData,
    isLoggedIn,
    isOnAppPage,
    isSearchFullWidth,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
    isSidebarForcedOpen:
      canForceSidebarOpen && isSidebarRendered ? isSidebarForcedOpen : false,
    isSidebarRendered,
    mode,
    onToggleSidebarVisibility,
    sessionData: sessionDataFromProps,
    setIsSidebarOpenedByTopbar,
    setIsSidebarForcedOpen,
    setIsSidebarRendered,
    shouldRenderCallToAction: !isCurrentUrlAnOnboardingPage(currentUrl),
    shouldRenderSearch,
    shouldRenderSidebar,
    shouldRenderAccessibleSidebar,
    shouldRenderTopbarLinks,
    shouldRenderTOS,
    shouldRenderUniversalNav: true,
    sidebarWidth,
    topbarSize,
    topNavData: topNav,
    urlPathPrefix,
    getFullyQualifiedURL: memoizedFullyQualifiedUrl,
    apiHostname: props.apiHostname || '',
  };
  return (
    <UniversalNavContext.Provider value={value}>
      {props.children}
    </UniversalNavContext.Provider>
  );
};
